import React, { useEffect, useMemo, useState } from 'react';
import { Panel } from './Panel/Panel';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import { SearchEntityType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import Search from '@Shared.Angular/flowingly.components/Search/Search';
import CustomEmailInput from '../CustomEmailInput/CustomEmailInput';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

interface Props {
  id?: string;
  initialSelectedItems?: IFlowinglySearchResult[];
  onChange: (items: IFlowinglySearchResult[]) => void;
  headerText: string;
  infoText?: string;
  userListAllowedPermissions?: string[];
  showCustomEmailInput?: boolean;
  showUser?: boolean;
  showTeam?: boolean;
}

export const SmartSelect = ({
  id,
  headerText,
  infoText,
  initialSelectedItems = [],
  userListAllowedPermissions = [],
  showCustomEmailInput = false,
  showUser = true,
  showTeam = true,
  onChange
}: Props) => {
  const smartSelectService = getService('smartSelectService');

  // Determine initial mode based on props
  const getInitialMode = () => {
    if (showCustomEmailInput) return SearchEntityType.CUSTOM_EMAIL;
    if (showUser) return SearchEntityType.USER;
    if (showTeam) return SearchEntityType.GROUP;
    return SearchEntityType.USER;
  };

  const [mode, setMode] = useState<SearchEntityType>(getInitialMode());
  const [availableItems, setAvailableItems] = useState<
    IFlowinglySearchResult[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedList, setSelectedList] =
    useState<IFlowinglySearchResult[]>(initialSelectedItems);
  const [groups, setGroups] = useState<IFlowinglySearchResult[] | null>(null);

  useEffect(() => {
    setSelectedList(initialSelectedItems);
  }, []);

  useEffect(() => {
    onChange(selectedList);
  }, [selectedList, onChange]);

  useEffect(() => {
    const debounceTimeout = 500;
    const handler = setTimeout(() => {
      fetchItems(
        mode,
        searchTerm,
        userListAllowedPermissions,
        setAvailableItems
      );
    }, debounceTimeout);

    return () => clearTimeout(handler);
  }, [mode, searchTerm]);

  const fetchItems = async (
    mode: SearchEntityType,
    searchTerm: string,
    userListAllowedPermissions: string[],
    setData: (data: IFlowinglySearchResult[]) => void
  ) => {
    switch (mode) {
      case SearchEntityType.USER: {
        const response = await smartSelectService.getUsers(
          searchTerm,
          userListAllowedPermissions
        );
        const items = response.data.users;
        smartSelectService.setHasMoreResults(response.data.hasMoreResults);
        setData(
          items.map((item) => ({
            id: item.id,
            displayName: item.fullName ?? '',
            searchEntityType: mode
          }))
        );
        break;
      }

      case SearchEntityType.GROUP: {
        let availableGroups = groups;
        if (!availableGroups) {
          const response = await smartSelectService.getGroups();
          const items = response.data.dataModel;
          availableGroups = items.map((item) => ({
            id: item.id,
            displayName: item.displayName ?? '',
            searchEntityType: mode
          }));
          setGroups(availableGroups);
        }

        const filtered = availableGroups.filter((item) =>
          item.displayName.toLowerCase().includes(searchTerm.toLowerCase())
        );

        setData(filtered);
        break;
      }

      case SearchEntityType.CUSTOM_EMAIL:
        // Custom email doesn't use a search list - it uses direct input
        // So we set an empty search list
        setData([]);
        break;

      default:
        setData([]);
        break;
    }
  };

  const hasMoreUserResults = () =>
    smartSelectService.getHasMoreResults() && mode === SearchEntityType.USER;

  const searchList = useMemo(
    () =>
      availableItems.filter((d) => !selectedList.some((s) => s.id === d.id)),
    [availableItems, selectedList]
  );

  const add = (item: IFlowinglySearchResult) =>
    setSelectedList((prev) =>
      prev.some((x) => x.id === item.id) ? prev : [...prev, item]
    );

  const remove = (item: IFlowinglySearchResult) =>
    setSelectedList((prev) => prev.filter((x) => x.id !== item.id));

  const addAll = () => setSelectedList((prev) => [...prev, ...searchList]);

  const removeAll = () => setSelectedList([]);

  const handleEmailAdd = (email: IFlowinglySearchResult) => {
    setSelectedList((prev) => [...prev, email]);
  };

  return (
    <div
      id={id}
      className={`smart-select-container ${
        mode === SearchEntityType.CUSTOM_EMAIL ? 'custom-email-mode' : ''
      }`}
    >
      <div className="left-section">
        <div className="header-block">
          {(showUser || showTeam) && (
            <div className="mode-toggle">
              <ul className="inline-list">
                {showUser && (
                  <li>
                    <input
                      type="radio"
                      name="mode"
                      id="user-mode"
                      value={SearchEntityType.USER}
                      checked={mode === SearchEntityType.USER}
                      onChange={() => {
                        setMode(SearchEntityType.USER);
                        setAvailableItems([]);
                      }}
                    />
                    <label htmlFor="user-mode">User</label>
                  </li>
                )}
                {showTeam && (
                  <li>
                    <input
                      type="radio"
                      name="mode"
                      id="team-mode"
                      value={SearchEntityType.GROUP}
                      checked={mode === SearchEntityType.GROUP}
                      onChange={() => {
                        setMode(SearchEntityType.GROUP);
                        setAvailableItems([]);
                      }}
                    />
                    <label htmlFor="team-mode">Team</label>
                  </li>
                )}
              </ul>
            </div>
          )}

          {mode === SearchEntityType.CUSTOM_EMAIL ? (
            <CustomEmailInput
              selectedEmails={selectedList}
              onEmailAdd={handleEmailAdd}
            />
          ) : (
            <div className="smart-select-search-container">
              <Search
                value=""
                placeholder="search"
                onValueChange={setSearchTerm}
              />
            </div>
          )}
        </div>

        {mode !== SearchEntityType.CUSTOM_EMAIL && (
          <>
            <div className="panel-wrapper">
              <Panel
                items={searchList}
                onItemClick={add}
                iconClass="fa-light fa-circle-plus fa-lg"
              />
            </div>

            {hasMoreUserResults() && (
              <div className="more-result-text">
                Please search to see additional users
              </div>
            )}
            {searchList.length > 0 && !hasMoreUserResults() && (
              <TextButton
                text="Add All"
                type="primary-text-only"
                classNames="add-all-button"
                onClick={addAll}
              />
            )}
          </>
        )}
      </div>

      <div className="right-section">
        <div className="header-block header-block-right">
          <h5 className="header-text">{headerText}</h5>
          <p className="info-text">{infoText}</p>
        </div>

        <div className="panel-wrapper">
          <Panel
            items={selectedList}
            onItemClick={remove}
            iconClass="fa-light fa-circle-minus fa-lg"
          />
        </div>
        {selectedList.length > 0 && !hasMoreUserResults() && (
          <TextButton
            text="Remove All"
            type="primary-text-only"
            classNames="remove-all-button"
            onClick={removeAll}
          />
        )}
      </div>
    </div>
  );
};
